import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from '../../context/AuthContext.js';
import { formatDate } from '../../utilities.js';
import LocationFilter from '../locaties/LocationFilter.js';
import './AllAbsences.css';

const EMPTY_PAGINATION = { page: 1, limit: 20, total: 0, totalPages: 1, hasNextPage: false, hasPrevPage: false };

function AllAbsences() {
  const [absences, setAbsences] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [locations, setLocations] = useState([]);
  const [locationFilter, setLocationFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [pagination, setPagination] = useState(EMPTY_PAGINATION);

  const token = sessionStorage.getItem('jwtToken');
  const { userInfo } = useAuth();
  const userRole = userInfo?.role || '';
  const canFilterLocations = ['super_admin', 'docent'].includes(userRole);
  const isParent = userRole === 'ouder';

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchAbsences = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/absence/getmyabsences`, {
        params: { page, limit, search: debouncedSearchTerm, location_id: locationFilter },
        headers: { Authorization: `Bearer ${token}` },
      });
      setAbsences(response.data.data || []);
      setPagination(response.data.pagination || { ...EMPTY_PAGINATION, limit });
    } catch (requestError) {
      console.error('Fout bij ophalen absenties:', requestError);
      setAbsences([]);
      setPagination({ ...EMPTY_PAGINATION, limit });
      setError('De absenties konden niet worden geladen. Probeer het opnieuw.');
    } finally {
      setIsLoading(false);
    }
  }, [token, page, limit, debouncedSearchTerm, locationFilter]);

  useEffect(() => { fetchAbsences(); }, [fetchAbsences]);

  useEffect(() => {
    if (!canFilterLocations) { setLocations([]); return; }
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/location/allLocations`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((response) => {
      const data = response.data.data || response.data;
      setLocations(Array.isArray(data) ? data : []);
    }).catch((requestError) => console.error('Fout bij ophalen locaties:', requestError));
  }, [canFilterLocations, token]);

  const getAbsenceId = (absence) => absence.id || absence.absence_id;
  const formatStatus = (status) => ({ In_behandeling: 'In behandeling', Ingediend: 'Ingediend', Verwerkt: 'Verwerkt', Afgewezen: 'Afgewezen' }[status] || status?.replace(/_/g, ' ') || '-');
  const statusClass = (status) => `ab-status ab-status-${String(status || '').toLowerCase().replace(/_/g, '-')}`;
  const canParentModify = (absence) => isParent && absence.status === 'Ingediend' && absence.submitted_by_user_id === userInfo?.user_id;
  const canEdit = (absence) => isParent ? canParentModify(absence) : true;
  const canDelete = (absence) => {
    if (['super_admin', 'locatie_admin'].includes(userRole)) return true;
    if (isParent) return canParentModify(absence);
    return userRole === 'docent' && absence.status === 'Ingediend' && absence.submitted_by_user_id === userInfo?.user_id;
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({ title: 'Absentie verwijderen?', text: 'Deze actie kan niet ongedaan worden gemaakt.', icon: 'warning', showCancelButton: true, confirmButtonText: 'Verwijderen', cancelButtonText: 'Annuleren', confirmButtonColor: '#c63d47', reverseButtons: true, focusCancel: true });
    if (!result.isConfirmed) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/absence/deleteabsence/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      await fetchAbsences();
      await Swal.fire({ toast: true, position: 'top', icon: 'success', title: 'Absentie verwijderd', showConfirmButton: false, timer: 1800, timerProgressBar: true });
    } catch (requestError) {
      console.error('Fout bij verwijderen absentie:', requestError);
      await Swal.fire({ icon: 'error', title: 'Verwijderen mislukt', text: requestError?.response?.data?.error || 'De absentie kon niet worden verwijderd.' });
    }
  };

  const handleProcessed = async (id) => {
    const result = await Swal.fire({ title: 'Markeren als verwerkt?', icon: 'question', showCancelButton: true, confirmButtonText: 'Ja, verwerken', cancelButtonText: 'Annuleren', confirmButtonColor: '#087f49', reverseButtons: true });
    if (!result.isConfirmed) return;
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/absence/tostatusverwerkt/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      await fetchAbsences();
      Swal.fire({ toast: true, position: 'top', icon: 'success', title: 'Status bijgewerkt', showConfirmButton: false, timer: 1800, timerProgressBar: true });
    } catch (requestError) {
      console.error('Fout bij status update:', requestError);
      Swal.fire({ icon: 'error', title: 'Bijwerken mislukt', text: 'De status kon niet worden bijgewerkt.' });
    }
  };

  const clearFilters = () => { setSearchTerm(''); setLocationFilter('all'); setLimit(20); setPage(1); };
  const startRecord = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const endRecord = pagination.total === 0 ? 0 : Math.min(pagination.page * pagination.limit, pagination.total);
  const pageNumbers = Array.from({ length: Math.min(5, pagination.totalPages || 1) }, (_, index) => {
    const start = Math.min(Math.max(1, (pagination.page || 1) - 2), Math.max(1, (pagination.totalPages || 1) - 4));
    return start + index;
  }).filter((number) => number <= (pagination.totalPages || 1));

  return (
    <main className="ab-page">
      <div className="ab-shell">
        <header className="ab-heading">
          <div><span className="ab-eyebrow">Aanwezigheid</span><h1>Absenties</h1><p>Bekijk en registreer afwezigheidsmeldingen.</p></div>
          <Link to="/newabsence" className="ab-primary-button"><i className="bi bi-plus-lg" /><span>Nieuwe absentie</span></Link>
        </header>

        <section className="ab-toolbar" aria-label="Absenties filteren">
          {!isParent && <label className="ab-search"><i className="bi bi-search" /><span className="visually-hidden">Zoek leerling</span><input type="search" placeholder="Zoek op naam" value={searchTerm} onChange={(event) => { setSearchTerm(event.target.value); setPage(1); }} /></label>}
          {canFilterLocations && <div className="ab-location-filter"><LocationFilter userRole={userRole} locationFilter={locationFilter} setLocationFilter={(value) => { setLocationFilter(value); setPage(1); }} locations={locations} /></div>}
          <label className="ab-limit"><span>Per pagina</span><select value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }}><option value={10}>10</option><option value={20}>20</option><option value={50}>50</option><option value={100}>100</option></select></label>
          {!isParent && <button type="button" className="ab-clear-button" onClick={clearFilters}><i className="bi bi-arrow-counterclockwise" />Filters wissen</button>}
        </section>

        <div className="ab-results-summary">{isLoading ? 'Absenties laden¦' : `${startRecord}–${endRecord} van ${pagination.total || 0} absenties`}</div>

        <section className="ab-panel" aria-busy={isLoading}>
          {isLoading && <div className="ab-state" role="status"><span className="ab-loader" /><strong>Absenties laden</strong><span>Even geduld</span></div>}
          {!isLoading && error && <div className="ab-state ab-state-error" role="alert"><span className="ab-state-icon"><i className="bi bi-exclamation-circle" /></span><strong>Laden mislukt</strong><span>{error}</span><button type="button" onClick={fetchAbsences}>Opnieuw proberen</button></div>}
          {!isLoading && !error && absences.length === 0 && <div className="ab-state"><span className="ab-state-icon"><i className="bi bi-calendar2-check" /></span><strong>Geen absenties gevonden</strong><span>Pas eventueel de filters aan of registreer een nieuwe absentie.</span></div>}
          {!isLoading && !error && absences.length > 0 && <div className="ab-list">
            <div className="ab-list-header"><span>Leerling</span><span>Datum en reden</span><span>Opmerking</span><span>Status</span><span>Acties</span></div>
            {absences.map((absence) => {
              const id = getAbsenceId(absence);
              return <article key={id} className={`ab-row ${absence.status === 'Ingediend' ? 'ab-row-new' : ''}`}>
                <div className="ab-student"><span className="ab-avatar">{(absence.student_firstname || '?')[0].toUpperCase()}</span><div><strong>{absence.student_firstname} {absence.student_lastname}</strong>{absence.status === 'Ingediend' && <small>Nieuwe melding</small>}</div></div>
                <div className="ab-date"><strong>{formatDate(absence.start_date)}</strong><span>{absence.reason || '-'}</span></div>
                <div className="ab-comment"><span className="ab-mobile-label">Opmerking</span>{absence.comment || <em>Geen opmerking</em>}</div>
                <div><span className={statusClass(absence.status)}>{formatStatus(absence.status)}</span></div>
                <div className="ab-actions">
                  {canEdit(absence) && <Link to={`/editabsence/${id}`} className="ab-icon-button" title="Absentie bewerken" aria-label="Absentie bewerken"><i className="bi bi-pencil" /></Link>}
                  {['super_admin', 'locatie_admin'].includes(userRole) && absence.status !== 'Verwerkt' && <button type="button" className="ab-icon-button ab-process-button" onClick={() => handleProcessed(id)} title="Markeren als verwerkt" aria-label="Markeren als verwerkt"><i className="bi bi-check2" /></button>}
                  {canDelete(absence) && <button type="button" className="ab-icon-button ab-delete-button" onClick={() => handleDelete(id)} title="Absentie verwijderen" aria-label="Absentie verwijderen"><i className="bi bi-trash3" /></button>}
                </div>
              </article>;
            })}
          </div>}
        </section>

        {!isLoading && !error && (pagination.totalPages || 1) > 1 && <nav className="ab-pagination" aria-label="Paginering">
          <button type="button" disabled={!pagination.hasPrevPage} onClick={() => setPage((value) => Math.max(value - 1, 1))}><i className="bi bi-chevron-left" /><span>Vorige</span></button>
          <div>{pageNumbers.map((number) => <button type="button" key={number} className={number === pagination.page ? 'ab-page-active' : ''} onClick={() => setPage(number)} aria-current={number === pagination.page ? 'page' : undefined}>{number}</button>)}</div>
          <button type="button" disabled={!pagination.hasNextPage} onClick={() => setPage((value) => value + 1)}><span>Volgende</span><i className="bi bi-chevron-right" /></button>
        </nav>}
      </div>
    </main>
  );
}

export default AllAbsences;